import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import RandevuFormModal from './RandevuFormModal';
import KayitTakipButonu from '../takip/KayitTakipButonu';
import { takipAc } from '../takip/takipOrtak';
import {
    RandevuDetay, SONUCLAR, durumMeta, aktifDurum, tarihEtiket, saatAraligi, sureDk, tarihOku, tarihStr, saatStr, yerelIso,
    haritaUrl, yolTarifiUrl, hataMesaji, randevuDegisti, sonucAdi, RANDEVU_DEGISTI_OLAYI,
} from './randevuOrtak';
import '../takip/takip.css';
import './randevu.css';

interface Props { randevuID: number; onKapat: () => void; }

type Panel = null | 'tamamla' | 'gelmedi' | 'ertele' | 'iptal';
const TAKIP_GUNLERI = [1, 2, 3, 5, 7, 14, 30];

const RandevuDetayModal: React.FC<Props> = ({ randevuID, onKapat }) => {
    const navigate = useNavigate();
    const [aktifID, setAktifID] = useState(randevuID);
    const [veri, setVeri] = useState<RandevuDetay | null>(null);
    const [yukleniyor, setYukleniyor] = useState(true);
    const [hata, setHata] = useState('');
    const [panel, setPanel] = useState<Panel>(null);
    const [duzenle, setDuzenle] = useState(false);
    const [isleniyor, setIsleniyor] = useState(false);
    // Tamamlama
    const [sonuc, setSonuc] = useState<string>('');
    const [gorusmeNotu, setGorusmeNotu] = useState('');
    const [takipVar, setTakipVar] = useState(true);
    const [takipGun, setTakipGun] = useState(3);
    const [takipBaslik, setTakipBaslik] = useState('');
    const [leadDurumlari, setLeadDurumlari] = useState<{ statusID: number; statusAdi: string }[]>([]);
    const [leadDurumID, setLeadDurumID] = useState<number | ''>('');
    // Erteleme / iptal
    const [yeniTarih, setYeniTarih] = useState('');
    const [yeniSaat, setYeniSaat] = useState('');
    const [neden, setNeden] = useState('');
    const [musteriyeBildir, setMusteriyeBildir] = useState(true);

    useEffect(() => { setAktifID(randevuID); }, [randevuID]);

    const yukle = useCallback(async (sessiz = false) => {
        if (!sessiz) setYukleniyor(true);
        setHata('');
        try {
            const r = await axiosInstance.get(`/api/Randevular/${aktifID}`);
            setVeri(r.data);
        } catch (e) { setVeri(null); setHata(hataMesaji(e, 'Randevu yüklenemedi.')); }
        finally { setYukleniyor(false); }
    }, [aktifID]);
    useEffect(() => { yukle(); setPanel(null); }, [yukle]);
    useEffect(() => {
        const h = () => yukle(true);
        window.addEventListener(RANDEVU_DEGISTI_OLAYI, h);
        return () => window.removeEventListener(RANDEVU_DEGISTI_OLAYI, h);
    }, [yukle]);
    useEffect(() => {
        const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !duzenle) onKapat(); };
        window.addEventListener('keydown', esc);
        return () => window.removeEventListener('keydown', esc);
    }, [onKapat, duzenle]);

    const r = veri?.randevu;
    const dm = durumMeta(r?.durum);

    const panelAc = (p: Panel) => {
        if (!r) return;
        setPanel(p);
        setHata('');
        if (p === 'tamamla' || p === 'gelmedi') {
            setSonuc(''); setGorusmeNotu(veri?.gorusmeNotu ?? ''); setTakipVar(p === 'tamamla'); setTakipGun(p === 'gelmedi' ? 1 : 3);
            setTakipBaslik(''); setLeadDurumID('');
            if (r.leadID && leadDurumlari.length === 0)
                axiosInstance.get('/api/LeadDurumlari').then(x => setLeadDurumlari((x.data as any[]).filter(d => d.aktifMi !== false))).catch(() => { });
        }
        if (p === 'ertele') {
            const d = tarihOku(r.baslangic); const yarin = new Date(d); yarin.setDate(yarin.getDate() + 1);
            setYeniTarih(tarihStr(yarin)); setYeniSaat(saatStr(d)); setNeden('');
        }
        if (p === 'iptal') setNeden('');
    };

    const durumGonder = async (durum: string, ek: any = {}, cakismaOnay = false): Promise<void> => {
        if (!r) return;
        setIsleniyor(true); setHata('');
        try {
            const res = await axiosInstance.post(`/api/Randevular/${r.randevuID}/durum`, { durum, ...ek, cakismaOnay, musteriyeBildir });
            if (res.data?.bildirimSonucu) alert(`Müşteri bildirimi: ${res.data.bildirimSonucu}`);
            setPanel(null);
            randevuDegisti();
            if (res.data?.yeniRandevuID) setAktifID(res.data.yeniRandevuID);
        } catch (e: any) {
            const d = e?.response?.data;
            if (e?.response?.status === 409 && d?.kod === 'ONAY_GEREKLI') {
                const detay = [...(d.cakismalar ?? []).map((c: any) => `${saatAraligi(c)} ${c.baslik}`), ...(d.uyarilar ?? [])].join('\n');
                if (window.confirm(`${d.message}\n${detay}\n\nYine de devam edilsin mi?`)) { setIsleniyor(false); return durumGonder(durum, ek, true); }
            } else setHata(hataMesaji(e));
        } finally { setIsleniyor(false); }
    };

    const tamamla = async () => {
        if (!r) return;
        setIsleniyor(true); setHata('');
        try {
            const res = await axiosInstance.post(`/api/Randevular/${r.randevuID}/tamamla`, {
                gelmedi: panel === 'gelmedi',
                sonuc: panel === 'tamamla' ? sonuc || null : null,
                gorusmeNotu: gorusmeNotu.trim() || null,
                takipGun: takipVar ? takipGun : null,
                takipBaslik: takipVar ? takipBaslik.trim() || null : null,
                leadDurumID: leadDurumID || null,
            });
            setPanel(null);
            randevuDegisti();
            if (res.data?.takipID) {
                window.dispatchEvent(new CustomEvent('yapicrm:takip-degisti'));
            }
        } catch (e) { setHata(hataMesaji(e)); }
        finally { setIsleniyor(false); }
    };

    const sil = async () => {
        if (!r) return;
        let kapsam = 'tek';
        if (r.tekrarGrupID) {
            const sonrakiler = window.confirm('Tekrarlayan randevu. Sonraki tekrarlar da silinsin mi?\n\nTamam: bu ve sonrakiler · İptal: yalnızca bu');
            kapsam = sonrakiler ? 'sonrakiler' : 'tek';
            if (!window.confirm(kapsam === 'tek' ? 'Yalnızca bu randevu silinsin mi?' : 'Bu ve sonraki tekrarlar silinsin mi?')) return;
        } else if (!window.confirm('Randevu kalıcı olarak silinsin mi? (Müşteriye bildirmek için silmek yerine iptal edin.)')) return;
        try {
            await axiosInstance.post(`/api/Randevular/delete/${r.randevuID}?kapsam=${kapsam}`);
            randevuDegisti();
            onKapat();
        } catch (e) { setHata(hataMesaji(e)); }
    };

    const musteriBildir = async (tur: string) => {
        if (!r) return;
        setIsleniyor(true);
        try {
            const res = await axiosInstance.post(`/api/Randevular/${r.randevuID}/musteri-bildir`, { tur, siteUrl: window.location.origin });
            alert(res.data?.message ?? 'Gönderildi');
        } catch (e) { alert(hataMesaji(e)); }
        finally { setIsleniyor(false); }
    };

    const onayLinki = veri ? (veri.onayLinki ?? `${window.location.origin}/randevu/${veri.musteriToken}`) : '';
    const linkKopyala = () => { navigator.clipboard?.writeText(onayLinki).then(() => alert('Onay / iptal bağlantısı kopyalandı.')).catch(() => window.prompt('Bağlantı:', onayLinki)); };
    const git = (url: string) => { onKapat(); navigate(url); };

    return (
        <div className="tkp-perde detay" onMouseDown={e => { if (e.target === e.currentTarget) onKapat(); }}>
            <div className="tkp-modal">
                <div className="tkp-modal-bas" style={r ? { borderBottom: `3px solid ${r.renk}` } : undefined}>
                    <i className={`fas ${r?.ikon || 'fa-calendar'}`} style={{ color: r?.renk ?? '#e67d21' }}></i>
                    <h5 title={r?.baslik}>{r?.baslik ?? 'Randevu'}</h5>
                    {r && <span className="rnd-durum" style={{ background: dm.zemin, color: dm.renk }}><i className={`fas ${dm.ikon}`}></i>{dm.ad}</span>}
                    <button className="tkp-kapat" onClick={onKapat}>×</button>
                </div>
                <div className="tkp-modal-govde">
                    {yukleniyor && <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }}></i></div>}
                    {hata && <div className="tkp-hata">{hata}</div>}
                    {!yukleniyor && veri && r && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                                <div style={{ fontSize: 16, fontWeight: 800, color: '#1a2440', textTransform: 'capitalize' }}>
                                    {tarihEtiket(r.baslangic, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                </div>
                                <span className="tkp-rozet notr" style={{ fontSize: 12 }}><i className="fas fa-clock"></i>{saatAraligi(r)} · {sureDk(r)} dk</span>
                                {r.turAdi && <span className="tkp-rozet" style={{ background: `${r.renk}1a`, color: r.renk, fontSize: 12 }}>{r.turAdi}</span>}
                                {r.onlineAlindi && <span className="tkp-rozet talep"><i className="fas fa-globe"></i>Online alındı</span>}
                                <span style={{ marginLeft: 'auto' }}><KayitTakipButonu tur="RANDEVU" anahtar={r.randevuID} baslik={`${tarihOku(r.baslangic).toLocaleDateString('tr-TR')} ${saatStr(tarihOku(r.baslangic))} ${r.baslik}`} /></span>
                            </div>

                            <div className="tkp-bilgi-izgara">
                                <div className="tkp-bilgi"><div className="e">Personel</div><div className="d">{r.personelAdi}</div></div>
                                <div className="tkp-bilgi">
                                    <div className="e">Görüşülecek kişi</div>
                                    <div className="d">
                                        {r.cariID ? <a href={`/musteriler/${r.cariID}`} onClick={e => { e.preventDefault(); git(`/musteriler/${r.cariID}`); }}>{r.cariAdi}</a>
                                            : r.leadID ? <a href={`/lead-takip?lead=${r.leadID}`} onClick={e => { e.preventDefault(); git(`/lead-takip?lead=${r.leadID}`); }}>{r.leadAdi} <span style={{ fontSize: 10, color: '#0891b2' }}>(potansiyel)</span></a>
                                                : veri.musteri.ad || '—'}
                                    </div>
                                    {(veri.musteri.telefon || veri.musteri.eposta) && (
                                        <div style={{ fontSize: 11.5, marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            {veri.musteri.telefon && <a href={`tel:${veri.musteri.telefon}`}><i className="fas fa-phone" style={{ marginRight: 3 }}></i>{veri.musteri.telefon}</a>}
                                            {veri.musteri.eposta && <a href={`mailto:${veri.musteri.eposta}`}><i className="fas fa-envelope" style={{ marginRight: 3 }}></i>{veri.musteri.eposta}</a>}
                                        </div>
                                    )}
                                </div>
                                {r.projeAdi && <div className="tkp-bilgi"><div className="e">Proje</div><div className="d"><a href={`/projeler/${r.projeID}`} onClick={e => { e.preventDefault(); git(`/projeler/${r.projeID}`); }}>{r.projeAdi}</a></div></div>}
                                {r.kaynakAdi && <div className="tkp-bilgi"><div className="e">Kaynak</div><div className="d">{r.kaynakAdi}</div></div>}
                                <div className="tkp-bilgi">
                                    <div className="e">Müşteri onayı</div>
                                    <div className="d" style={{ color: r.musteriOnay === 'ONAYLADI' ? '#16a34a' : r.musteriOnay === 'IPTAL_ETTI' ? '#dc2626' : '#94a3b8' }}>
                                        {r.musteriOnay === 'ONAYLADI' ? 'Onayladı' : r.musteriOnay === 'IPTAL_ETTI' ? 'İptal etti' : veri.musteriBildirim ? 'Bekleniyor' : 'Bildirim kapalı'}
                                    </div>
                                </div>
                                {veri.tekrarSayisi > 1 && <div className="tkp-bilgi"><div className="e">Tekrar</div><div className="d">{veri.tekrarSayisi} randevuluk seri</div></div>}
                                {veri.olusturanAdi && <div className="tkp-bilgi"><div className="e">Oluşturan</div><div className="d">{veri.olusturanAdi}</div></div>}
                            </div>

                            {r.konum && (
                                <div className="tkp-bolum">
                                    <div className="tkp-bolum-bas"><i className="fas fa-map-marker-alt"></i>Konum</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 13, color: '#374151', flex: 1, minWidth: 200 }}>
                                            {/^https?:/i.test(r.konum) ? <a href={r.konum} target="_blank" rel="noreferrer">{r.konum}</a> : r.konum}
                                        </span>
                                        {!/^https?:/i.test(r.konum) && (
                                            <>
                                                <a className="tkp-btn kucuk" href={haritaUrl(r.konum)} target="_blank" rel="noreferrer"><i className="fas fa-map"></i>Harita</a>
                                                <a className="tkp-btn kucuk" href={yolTarifiUrl(r.konum)} target="_blank" rel="noreferrer"><i className="fas fa-route"></i>Yol tarifi</a>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {veri.aciklama && (
                                <div className="tkp-bolum">
                                    <div className="tkp-bolum-bas"><i className="fas fa-align-left"></i>Açıklama</div>
                                    <div className="tkp-aciklama">{veri.aciklama}</div>
                                </div>
                            )}

                            {(r.durum === 'TAMAMLANDI' || r.durum === 'GELMEDI') && (r.sonuc || veri.gorusmeNotu) && (
                                <div className="tkp-bolum">
                                    <div className="tkp-bolum-bas"><i className="fas fa-clipboard-check"></i>Görüşme sonucu {r.sonuc && <span className="tkp-rozet tamam" style={{ textTransform: 'none' }}>{sonucAdi(r.sonuc)}</span>}</div>
                                    {veri.gorusmeNotu && <div className="tkp-aciklama">{veri.gorusmeNotu}</div>}
                                    {veri.takipID && <button className="tkp-btn kucuk" style={{ marginTop: 8 }} onClick={() => takipAc(veri.takipID!)}><i className="fas fa-tasks"></i>Oluşan takibi aç</button>}
                                </div>
                            )}
                            {veri.iptalNedeni && (r.durum === 'IPTAL' || r.durum === 'ERTELENDI') && (
                                <div className="tkp-bolum">
                                    <div className="tkp-bolum-bas"><i className="fas fa-info-circle"></i>{r.durum === 'IPTAL' ? 'İptal nedeni' : 'Erteleme nedeni'}</div>
                                    <div className="tkp-aciklama">{veri.iptalNedeni}</div>
                                </div>
                            )}

                            {/* İşlem panelleri */}
                            {(panel === 'tamamla' || panel === 'gelmedi') && (
                                <div className="tkp-bolum" style={{ background: '#f8fafc', borderRadius: 10, padding: 12 }}>
                                    <div className="tkp-bolum-bas"><i className={`fas ${panel === 'gelmedi' ? 'fa-user-slash' : 'fa-check-circle'}`}></i>{panel === 'gelmedi' ? 'Müşteri gelmedi' : 'Randevuyu tamamla'}</div>
                                    {panel === 'tamamla' && (
                                        <div className="tkp-alan">
                                            <label className="tkp-etiket">Sonuç</label>
                                            <div className="rnd-secim">
                                                {SONUCLAR.map(s => (
                                                    <button key={s.s} type="button" className={sonuc === s.s ? 'aktif' : ''} onClick={() => setSonuc(sonuc === s.s ? '' : s.s)}>
                                                        <i className={`fas ${s.ikon}`}></i>{s.ad}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <div className="tkp-alan">
                                        <label className="tkp-etiket">Görüşme notu</label>
                                        <textarea className="tkp-girdi" rows={3} value={gorusmeNotu} onChange={e => setGorusmeNotu(e.target.value)}
                                            placeholder={panel === 'gelmedi' ? 'Ulaşılamadı, tekrar aranacak…' : 'Görüşmede konuşulanlar, müşterinin talepleri…'} />
                                        {(r.cariID || r.leadID) && <div className="tkp-ipucu">Not, {r.cariID ? 'müşteri kartına' : 'potansiyel müşterinin geçmişine'} de işlenir.</div>}
                                    </div>
                                    {r.leadID && leadDurumlari.length > 0 && (
                                        <div className="tkp-alan">
                                            <label className="tkp-etiket">Potansiyel müşteri aşaması (satış hunisi)</label>
                                            <select className="tkp-girdi" value={leadDurumID} onChange={e => setLeadDurumID(e.target.value ? Number(e.target.value) : '')}>
                                                <option value="">Değiştirme</option>
                                                {leadDurumlari.map(d => <option key={d.statusID} value={d.statusID}>{d.statusAdi}</option>)}
                                            </select>
                                        </div>
                                    )}
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={takipVar} onChange={e => setTakipVar(e.target.checked)} style={{ accentColor: '#e67d21' }} />
                                        Takip oluştur:
                                        <select className="tkp-girdi" style={{ width: 'auto', padding: '3px 6px' }} value={takipGun} disabled={!takipVar} onChange={e => setTakipGun(Number(e.target.value))}>
                                            {TAKIP_GUNLERI.map(g => <option key={g} value={g}>{g} gün sonra</option>)}
                                        </select>
                                    </label>
                                    {takipVar && (
                                        <input className="tkp-girdi" style={{ marginTop: 6 }} value={takipBaslik} onChange={e => setTakipBaslik(e.target.value)}
                                            placeholder={`Takip başlığı (boş: "Randevu sonrası ara: ${r.musteriAdi ?? r.baslik}")`} />
                                    )}
                                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                                        <button className="tkp-btn" onClick={() => setPanel(null)}>Vazgeç</button>
                                        <button className={`tkp-btn ${panel === 'gelmedi' ? 'ana' : 'yesil'}`} onClick={tamamla} disabled={isleniyor}>
                                            <i className={`fas ${isleniyor ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>Kaydet
                                        </button>
                                    </div>
                                </div>
                            )}
                            {panel === 'ertele' && (
                                <div className="tkp-bolum" style={{ background: '#fffbeb', borderRadius: 10, padding: 12 }}>
                                    <div className="tkp-bolum-bas"><i className="fas fa-history"></i>Randevuyu ertele</div>
                                    <div className="tkp-satir">
                                        <input type="date" className="tkp-girdi" value={yeniTarih} onChange={e => setYeniTarih(e.target.value)} />
                                        <input type="time" className="tkp-girdi" value={yeniSaat} step={300} onChange={e => setYeniSaat(e.target.value)} />
                                    </div>
                                    <input className="tkp-girdi" style={{ marginTop: 6 }} value={neden} onChange={e => setNeden(e.target.value)} placeholder="Erteleme nedeni (isteğe bağlı)" />
                                    {veri.musteriBildirim && (
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, marginTop: 6 }}>
                                            <input type="checkbox" checked={musteriyeBildir} onChange={e => setMusteriyeBildir(e.target.checked)} />Müşteriye yeni tarihi bildir
                                        </label>
                                    )}
                                    <div className="tkp-ipucu">Bu randevu "Ertelendi" olarak kalır, yeni tarihte aynı bilgilerle yeni randevu açılır.</div>
                                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                                        <button className="tkp-btn" onClick={() => setPanel(null)}>Vazgeç</button>
                                        <button className="tkp-btn turuncu" disabled={isleniyor || !yeniTarih || !yeniSaat} onClick={() => {
                                            const bas = new Date(`${yeniTarih}T${yeniSaat}:00`);
                                            durumGonder('ERTELENDI', { iptalNedeni: neden || null, yeniBaslangic: yerelIso(bas), yeniBitis: yerelIso(new Date(bas.getTime() + sureDk(r) * 60000)) });
                                        }}><i className="fas fa-history"></i>Ertele</button>
                                    </div>
                                </div>
                            )}
                            {panel === 'iptal' && (
                                <div className="tkp-bolum" style={{ background: '#fef2f2', borderRadius: 10, padding: 12 }}>
                                    <div className="tkp-bolum-bas"><i className="fas fa-times-circle"></i>Randevuyu iptal et</div>
                                    <input className="tkp-girdi" value={neden} onChange={e => setNeden(e.target.value)} placeholder="İptal nedeni" autoFocus />
                                    {veri.musteriBildirim && (
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, marginTop: 6 }}>
                                            <input type="checkbox" checked={musteriyeBildir} onChange={e => setMusteriyeBildir(e.target.checked)} />Müşteriye iptal bildirimi gönder
                                        </label>
                                    )}
                                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                                        <button className="tkp-btn" onClick={() => setPanel(null)}>Vazgeç</button>
                                        <button className="tkp-btn kirmizi" disabled={isleniyor} onClick={() => durumGonder('IPTAL', { iptalNedeni: neden || null })}><i className="fas fa-times"></i>İptal Et</button>
                                    </div>
                                </div>
                            )}

                            {/* Müşteri bildirimi */}
                            {aktifDurum(r.durum) && (
                                <div className="tkp-bolum">
                                    <div className="tkp-bolum-bas"><i className="fas fa-paper-plane"></i>Müşteri</div>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        <button className="tkp-btn kucuk" disabled={isleniyor} onClick={() => musteriBildir('HATIRLATMA')}><i className="fas fa-bell"></i>Hatırlatma gönder (SMS / e-posta)</button>
                                        <button className="tkp-btn kucuk" onClick={linkKopyala}><i className="fas fa-link"></i>Onay / iptal bağlantısını kopyala</button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {!yukleniyor && veri && r && veri.duzenleyebilir && !panel && (
                    <div className="tkp-modal-alt">
                        <button className="tkp-btn kirmizi" onClick={sil} style={{ marginRight: 'auto' }} title="Sil"><i className="fas fa-trash"></i></button>
                        {aktifDurum(r.durum) ? (
                            <>
                                <button className="tkp-btn" onClick={() => setDuzenle(true)}><i className="fas fa-pen"></i>Düzenle / Devret</button>
                                <button className="tkp-btn" onClick={() => panelAc('ertele')}><i className="fas fa-history"></i>Ertele</button>
                                <button className="tkp-btn kirmizi" onClick={() => panelAc('iptal')}><i className="fas fa-times"></i>İptal</button>
                                {r.durum === 'PLANLANDI' && <button className="tkp-btn" onClick={() => durumGonder('ONAYLANDI')}><i className="fas fa-calendar-check"></i>Onaylandı</button>}
                                <button className="tkp-btn" onClick={() => panelAc('gelmedi')}><i className="fas fa-user-slash"></i>Gelmedi</button>
                                <button className="tkp-btn yesil" onClick={() => panelAc('tamamla')}><i className="fas fa-check"></i>Tamamla</button>
                            </>
                        ) : (
                            <button className="tkp-btn" onClick={() => durumGonder('PLANLANDI')}><i className="fas fa-undo"></i>Yeniden planla</button>
                        )}
                    </div>
                )}
            </div>
            {duzenle && veri && <RandevuFormModal mevcut={veri} onKapat={() => setDuzenle(false)} />}
        </div>
    );
};

export default RandevuDetayModal;
